import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useAuthStore } from '@carouselgg/auth/store'
import { Card } from '@carouselgg/nexus/card'
import { Text } from '@carouselgg/nexus/text'
import { Button } from '@carouselgg/nexus/button'
import { Badge } from '@carouselgg/nexus/badge'
import { toast } from '@carouselgg/nexus/toast'
import { getInviteByToken, acceptInvite, declineInvite } from '../api/invites'
import type { InviteDetail } from '../api/invites'
import styles from './InvitePage.module.css'

type PageState = 'loading' | 'invalid' | 'unauthenticated' | 'email_mismatch' | 'ready' | 'accepted' | 'declined'

export function InvitePage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isLoading = useAuthStore((s) => s.isLoading)

  const [invite, setInvite] = useState<InviteDetail | null>(null)
  const [state, setState] = useState<PageState>('loading')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!token) {
      setState('invalid')
      return
    }

    getInviteByToken(token)
      .then((data) => {
        setInvite(data)
        if (data.status !== 'pending') {
          setState('invalid')
        } else if (isLoading) {
          // Wait for auth to resolve
        } else if (!isAuthenticated) {
          setState('unauthenticated')
        } else if (user && user.email.toLowerCase() !== data.email.toLowerCase()) {
          setState('email_mismatch')
        } else {
          setState('ready')
        }
      })
      .catch(() => {
        setState('invalid')
      })
  }, [token, isAuthenticated, isLoading, user])

  // Re-evaluate state when auth finishes loading
  useEffect(() => {
    if (isLoading || !invite || invite.status !== 'pending') return
    if (!isAuthenticated) {
      setState('unauthenticated')
    } else if (user && user.email.toLowerCase() !== invite.email.toLowerCase()) {
      setState('email_mismatch')
    } else {
      setState('ready')
    }
  }, [isLoading, isAuthenticated, user, invite])

  async function handleAccept() {
    if (!token) return
    setSubmitting(true)
    try {
      const result = await acceptInvite(token)
      setInvite(result)
      setState('accepted')
      toast.success(`You've joined ${result.organization.name}!`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to accept invitation')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDecline() {
    if (!token) return
    setSubmitting(true)
    try {
      await declineInvite(token)
      setState('declined')
      toast.success('Invitation declined')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to decline invitation')
    } finally {
      setSubmitting(false)
    }
  }

  if (state === 'loading') {
    return (
      <div className={styles.page}>
        <Text muted>Loading invitation...</Text>
      </div>
    )
  }

  if (state === 'invalid') {
    const statusMessage = invite?.status === 'expired' || invite?.status === 'revoked'
      ? `This invitation has been ${invite.status}.`
      : invite?.status === 'accepted'
        ? 'This invitation has already been accepted.'
        : invite?.status === 'declined'
          ? 'This invitation has been declined.'
          : 'This invitation is no longer valid.'

    return (
      <div className={styles.page}>
        <Card>
          <div className={styles.content}>
            <Text as="h1" size="xl" weight="bold">Invitation Unavailable</Text>
            <Text muted>{statusMessage}</Text>
            <Button variant="primary" onClick={() => navigate('/')}>
              Go Home
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  if (state === 'accepted') {
    return (
      <div className={styles.page}>
        <Card>
          <div className={styles.content}>
            <Text as="h1" size="xl" weight="bold">Welcome!</Text>
            <Text>
              You've joined <strong>{invite?.organization.name}</strong> as a{' '}
              <strong>{invite?.role}</strong>
              {invite?.team && (
                <> and been added to team <strong>{invite.team.name}</strong> as <strong>{invite.team.role}</strong></>
              )}
              .
            </Text>
            <Button
              variant="primary"
              onClick={() => navigate(`/org/${invite?.organization.slug}`)}
            >
              Go to {invite?.organization.name}
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  if (state === 'declined') {
    return (
      <div className={styles.page}>
        <Card>
          <div className={styles.content}>
            <Text as="h1" size="xl" weight="bold">Invitation Declined</Text>
            <Text muted>You've declined the invitation.</Text>
            <Button variant="primary" onClick={() => navigate('/')}>
              Go Home
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <Card>
        <div className={styles.content}>
          {invite?.organization.logo_url && (
            <img
              src={invite.organization.logo_url}
              alt={invite.organization.name}
              className={styles.orgLogo}
            />
          )}
          <Text as="h1" size="xl" weight="bold">
            You've been invited to join {invite?.organization.name}
          </Text>
          <div className={styles.details}>
            <div className={styles.detailRow}>
              <Text size="sm" muted>Organization</Text>
              <Text size="sm" weight="medium">{invite?.organization.name}</Text>
            </div>
            <div className={styles.detailRow}>
              <Text size="sm" muted>Type</Text>
              <Badge size="sm">{invite?.organization.type}</Badge>
            </div>
            <div className={styles.detailRow}>
              <Text size="sm" muted>Role</Text>
              <Badge size="sm" variant="info">{invite?.role}</Badge>
            </div>
            {invite?.team && (
              <>
                <div className={styles.detailRow}>
                  <Text size="sm" muted>Team</Text>
                  <Text size="sm" weight="medium">{invite.team.name}</Text>
                </div>
                <div className={styles.detailRow}>
                  <Text size="sm" muted>Team Role</Text>
                  <Badge size="sm" variant="success">{invite.team.role}</Badge>
                </div>
              </>
            )}
          </div>

          {state === 'unauthenticated' && (
            <>
              <Text size="sm" muted>
                Sign in or create an account to accept this invitation.
              </Text>
              <div className={styles.actions}>
                <Button
                  variant="primary"
                  onClick={() => navigate(`/login?redirect=/invites/${token}`)}
                >
                  Log In
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => navigate(`/signup?redirect=/invites/${token}`)}
                >
                  Create Account
                </Button>
              </div>
            </>
          )}

          {state === 'email_mismatch' && (
            <div className={styles.mismatch}>
              <Text size="sm">
                This invitation was sent to <strong>{invite?.email}</strong>.
              </Text>
              <Text size="sm" muted>
                You're signed in as <strong>{user?.email}</strong>. Please sign in with the correct account.
              </Text>
            </div>
          )}

          {state === 'ready' && (
            <div className={styles.actions}>
              <Button
                variant="primary"
                onClick={handleAccept}
                disabled={submitting}
              >
                {submitting ? 'Accepting...' : 'Accept Invitation'}
              </Button>
              <Button
                variant="ghost"
                onClick={handleDecline}
                disabled={submitting}
              >
                Decline
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}
